import {
  Box,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  InputLabel,
  Divider,
  Table,
  TableFooter,
} from '@mui/material';
import { useState, useMemo, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';

import { formatCurrency, formatPercent, handleApiError } from '~/utils/helper';
import { IProjectReport } from '~/utils/interface/report';
import { HeadCellType } from '~/utils/types/common';
import { useTranslation } from 'react-i18next';
import { BaseDatePicker, BaseHeaderTitle, BaseRow, BaseSelect } from '~/components/common';
import dayjs from 'dayjs';
import { handleGetAccountsSelection } from '~/thunks/account/accountThunk';
import { handleGetStaffSaleReport } from '~/thunks/report/reportThunk';
import { handleGetMasterData } from '~/thunks/master/masterThunk';
import { MASTER_DATA_JOB_STATUSES } from '~/utils/constants/master';

const StaffSaleReport = () => {
  const { t } = useTranslation('admin/report');
  const dispatch = useAppDispatch();

  const currentDate = new Date();
  const end = currentDate.toISOString().split('T')[0];
  const start = new Date(currentDate.setMonth(currentDate.getMonth() - 1)).toISOString().split('T')[0];
  const headCells: Array<HeadCellType> = useMemo(
    () => [
      { id: 'projectId', label: t('properties.projectId') },

      { id: 'clientName', label: t('properties.clientName') },
      { id: 'referralSource', label: t('properties.referralSource') },
      { id: 'estimatedAmount', label: t('properties.estimatedAmount') },
      { id: 'invoiceAmount', label: t('properties.invoiceAmount') },
      { id: 'paidAmount', label: t('properties.paidAmount') },
      { id: 'projectCosts', label: t('properties.projectCosts') },

      { id: 'grossProfit', label: t('properties.grossProfit') },
      { id: 'profitMargin', label: t('properties.profitMargin') },
    ],
    [t]
  );

  const { selectionAccounts } = useAppSelector((state) => state.account);
  const [startDate, setStartDate] = useState<string>(dayjs().format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState<string>(dayjs().format('YYYY-MM-DD'));
  const { jobStatuses } = useAppSelector((state) => state.master);
  const [selectedAccount, setSelectedAccount] = useState<Array<string>>([]);
  const [selectedAssignees, setSelectedAssignees] = useState<Array<string>>([]);
  const [status, setStatus] = useState<Array<string>>([]);
  const [job, setJob] = useState<Array<IProjectReport>>([]);
  const optionsWithAll = [{ id: 'all', name: 'All' }, ...selectionAccounts];
  const optionsAssginees = [{ id: 'all', name: 'All' }, ...selectionAccounts];
  const optionsWithAllStatus = [{ id: 'all', name: 'All' }, ...(jobStatuses ?? [])];
  const handleSearch = () => {
    const params: any = { startDate, endDate };

    if (!selectedAccount.includes('all')) {
      params.createdBy = selectedAccount;
    }

    if (!status.includes('all')) {
      params.statusId = status;
    }

    if (!selectedAssignees.includes('all')) {
      params.assignees = selectedAssignees;
    }

    dispatch(handleGetStaffSaleReport(params))
      .unwrap()
      .then((response) => {
        setJob(response.responses || []);
      })
      .catch(handleApiError);
  };

  useEffect(() => {
    dispatch(handleGetAccountsSelection({}))
      .unwrap()
      .catch((e) => {
        handleApiError(e);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);
  useEffect(() => {
    dispatch(
      handleGetMasterData({
        type: [MASTER_DATA_JOB_STATUSES],
      })
    );
  }, [dispatch]);
  useEffect(() => {
    dispatch(handleGetStaffSaleReport({ startDate: start, endDate: end }))
      .unwrap()
      .then((response) => {
        setStartDate(start);
        setEndDate(end);
        setStatus(['all']);
        setSelectedAccount(['all']);
        setSelectedAssignees(['all']);
        setJob(response.responses || []);
      })
      .catch(handleApiError);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box>
      <BaseHeaderTitle title={t('title.staffSale')} />

      <Divider className='mb-[20px] mt-[20px]' />
      <BaseRow className='gap-[20px] '>
        <Box className='w-[900px]'>
          <InputLabel>{t('properties.startDate')}</InputLabel>
          <BaseDatePicker
            value={startDate}
            onChange={(value) => setStartDate(dayjs(value).format('YYYY-MM-DD'))}
            dateFormat='DD MMM, YYYY'
          />
        </Box>
        <Box className='w-[900px]'>
          <InputLabel>{t('properties.endDate')}</InputLabel>
          <BaseDatePicker
            value={endDate}
            onChange={(value) => setEndDate(dayjs(value).format('YYYY-MM-DD'))}
            dateFormat='DD MMM, YYYY'
          />
        </Box>
        <Box className='w-[900px]'>
          <InputLabel>{t('properties.createdBy')}</InputLabel>
          <BaseSelect
            options={optionsWithAll}
            value={selectedAccount}
            onChange={(event) => {
              const selectedValue = event.target.value;

              if (selectedValue.includes('all') && !selectedAccount.includes('all')) {
                setSelectedAccount(['all']);
              } else if (selectedValue !== 'all' && selectedAccount.includes('all')) {
                setSelectedAccount(selectedValue.filter((value: any) => value !== 'all'));
              } else {
                setSelectedAccount(selectedValue);
              }
            }}
            label={t('properties.createdBy')}
            placeholder={t('properties.createdBy')}
            multiple
          />
        </Box>
        <Box className='w-[900px]'>
          <InputLabel>{t('properties.assginees')}</InputLabel>
          <BaseSelect
            options={optionsAssginees}
            value={selectedAssignees}
            onChange={(event) => {
              const selectedValue = event.target.value;

              if (selectedValue.includes('all') && !selectedAssignees.includes('all')) {
                setSelectedAssignees(['all']);
              } else if (selectedValue !== 'all' && selectedAssignees.includes('all')) {
                setSelectedAssignees(selectedValue.filter((value: any) => value !== 'all'));
              } else {
                setSelectedAssignees(selectedValue);
              }
            }}
            label={t('properties.assginees')}
            placeholder={t('properties.assginees')}
            multiple
          />
        </Box>
        <Box className='w-[900px]'>
          <InputLabel>{t('properties.projectStatus')}</InputLabel>
          <BaseSelect
            options={optionsWithAllStatus}
            value={status}
            onChange={(event) => {
              const selectedValue = event.target.value;

              if (selectedValue.includes('all') && !status.includes('all')) {
                setStatus(['all']);
              } else if (selectedValue !== 'all' && status.includes('all')) {
                setStatus(selectedValue.filter((value: any) => value !== 'all'));
              } else {
                setStatus(selectedValue);
              }
            }}
            label={t('properties.projectStatus')}
            placeholder={t('properties.projectStatus')}
            multiple
          />
        </Box>
        <Box className='w-[200px] mt-[12px]'>
          <InputLabel></InputLabel>
          <Button variant='contained' color='primary' onClick={handleSearch}>
            {t('search')}
          </Button>
        </Box>
      </BaseRow>

      <Divider className='mb-[20px] mt-[20px]' />
      <TableContainer className='max-h-[640px] overflow-auto'>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {headCells.map((cell) => (
                <TableCell key={cell.id} className='bg-[#F0F7FF] text-[#758CA5] text-lg font-bold w-1/9'>
                  {cell.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {job.map((row, index) => (
              <TableRow key={index}>
                <TableCell className='w-1/9'>{row.name}</TableCell>
                <TableCell className='w-1/9'>{row.customerName}</TableCell>
                <TableCell className='w-1/9'>{row.referralSource}</TableCell>
                <TableCell className='w-1/9'>{formatCurrency(row.totalEstimates)}</TableCell>
                <TableCell className='w-1/9'>{formatCurrency(row.totalInvoices)}</TableCell>
                <TableCell className='w-1/9'>{formatCurrency(row.totalPaid)}</TableCell>
                <TableCell className='w-1/9'>{formatCurrency(row.projectCosts)}</TableCell>
                <TableCell className='w-1/9'>{formatCurrency(row.grossProfit)}</TableCell>

                <TableCell className='w-1/9'>{formatPercent(row.profitMargin)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
          {/* Total row */}
          <TableFooter>
            <TableRow className='sticky bottom-0 bg-[#F0F7FF]'>
              <TableCell className='font-bold text-[#758CA5]'>{job.filter((row) => row.name).length}</TableCell>
              <TableCell colSpan={2} />
              <TableCell className='font-bold text-[#758CA5]'>
                {formatCurrency(job.reduce((sum, row) => sum + (row.totalEstimates || 0), 0))}
              </TableCell>
              <TableCell className='font-bold text-[#758CA5]'>
                {formatCurrency(job.reduce((sum, row) => sum + (row.totalInvoices || 0), 0))}
              </TableCell>
              <TableCell className='font-bold text-[#758CA5]'>
                {formatCurrency(job.reduce((sum, row) => sum + (row.totalPaid || 0), 0))}
              </TableCell>
              <TableCell className='font-bold text-[#758CA5]'>
                {formatCurrency(job.reduce((sum, row) => sum + (row.projectCosts || 0), 0))}
              </TableCell>
              <TableCell className='font-bold text-[#758CA5]'>
                {formatCurrency(job.reduce((sum, row) => sum + (row.grossProfit || 0), 0))}
              </TableCell>
              <TableCell className='font-bold text-[#758CA5]'>
                {formatPercent(job.reduce((sum, row) => sum + (row.profitMargin || 0), 0) / job.length)}
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default StaffSaleReport;
